import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../utils/api';
import './ProfilePicModal.css';

const resolveProfilePicUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (!base) return url;
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const ProfilePicModal = ({ isOpen, currentPicUrl, initials, onClose, onSave, uploading, userId, isKid, parentId }) => {
  const [mode, setMode] = useState('view'); // view | crop
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const cropAreaRef = useRef(null);
  const previewUrlRef = useRef(null);

  // Crop state
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, size: 150 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imgDims, setImgDims] = useState({ w: 0, h: 0, natW: 0, natH: 0 });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setMode('view');
      setPreviewFile(null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
    }
  }, [isOpen]);

  // Clean up preview URL on unmount only (ref-based to survive StrictMode)
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { alert('Only JPEG, PNG, GIF, and WebP images are allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('File size must be under 5MB'); return; }
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewFile(file);
    setPreviewUrl(url);
    setMode('crop');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    const dispW = img.width;
    const dispH = img.height;
    setImgDims({ w: dispW, h: dispH, natW, natH });
    const initSize = Math.min(dispW, dispH) * 0.7;
    setCropBox({
      x: (dispW - initSize) / 2,
      y: (dispH - initSize) / 2,
      size: initSize
    });
  };

  const getPointerPos = useCallback((e, container) => {
    const rect = container.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const handleCropMouseDown = useCallback((e) => {
    e.preventDefault();
    const container = cropAreaRef.current;
    if (!container) return;
    const pos = getPointerPos(e, container);
    // Check if near bottom-right corner (resize handle)
    const cornerX = cropBox.x + cropBox.size;
    const cornerY = cropBox.y + cropBox.size;
    if (Math.abs(pos.x - cornerX) < 18 && Math.abs(pos.y - cornerY) < 18) {
      setResizing(true);
      setDragStart({ x: pos.x, y: pos.y });
    } else if (pos.x >= cropBox.x && pos.x <= cropBox.x + cropBox.size &&
               pos.y >= cropBox.y && pos.y <= cropBox.y + cropBox.size) {
      setDragging(true);
      setDragStart({ x: pos.x - cropBox.x, y: pos.y - cropBox.y });
    }
  }, [cropBox, getPointerPos]);

  const handleCropMouseMove = useCallback((e) => {
    if (!dragging && !resizing) return;
    e.preventDefault();
    const container = cropAreaRef.current;
    if (!container) return;
    const pos = getPointerPos(e, container);

    if (resizing) {
      const dx = pos.x - cropBox.x;
      const dy = pos.y - cropBox.y;
      let newSize = Math.max(dx, dy);
      newSize = Math.max(50, newSize);
      newSize = Math.min(newSize, imgDims.w - cropBox.x, imgDims.h - cropBox.y);
      setCropBox(prev => ({ ...prev, size: newSize }));
    } else if (dragging) {
      let nx = pos.x - dragStart.x;
      let ny = pos.y - dragStart.y;
      nx = Math.max(0, Math.min(nx, imgDims.w - cropBox.size));
      ny = Math.max(0, Math.min(ny, imgDims.h - cropBox.size));
      setCropBox(prev => ({ ...prev, x: nx, y: ny }));
    }
  }, [dragging, resizing, dragStart, cropBox, imgDims, getPointerPos]);

  const handleCropMouseUp = useCallback(() => {
    setDragging(false);
    setResizing(false);
  }, []);

  useEffect(() => {
    if (mode === 'crop') {
      window.addEventListener('mouseup', handleCropMouseUp);
      window.addEventListener('touchend', handleCropMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleCropMouseUp);
        window.removeEventListener('touchend', handleCropMouseUp);
      };
    }
  }, [mode, handleCropMouseUp]);

  const applyCrop = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const scaleX = imgDims.natW / imgDims.w;
    const scaleY = imgDims.natH / imgDims.h;

    const sx = cropBox.x * scaleX;
    const sy = cropBox.y * scaleY;
    const sSize = cropBox.size * Math.max(scaleX, scaleY);

    const outputSize = 400;
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, outputSize, outputSize);

    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], 'profile-cropped.jpg', { type: 'image/jpeg' });
        onSave(croppedFile);
      }
    }, 'image/jpeg', 0.92);
  };

  const handleSaveWithoutCrop = () => {
    if (previewFile) {
      onSave(previewFile);
    }
  };

  const handleCropExisting = async () => {
    if (currentPicUrl && userId) {
      try {
        // Fetch via backend proxy to avoid CORS issues with blob storage
        const params = isKid ? `?type=kid&parent_id=${parentId}` : '';
        const response = await api.get(`/api/profile-pic-data/${userId}${params}`, { responseType: 'blob' });
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const blobUrl = URL.createObjectURL(response.data);
        previewUrlRef.current = blobUrl;
        setPreviewUrl(blobUrl);
        setPreviewFile(null); // flag: cropping existing
        setMode('crop');
      } catch (err) {
        console.error('Failed to load image for cropping:', err);
        alert('Could not load image for cropping. Please try uploading a new photo instead.');
      }
    }
  };

  const applyCropExisting = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const scaleX = imgDims.natW / imgDims.w;
    const scaleY = imgDims.natH / imgDims.h;

    const sx = cropBox.x * scaleX;
    const sy = cropBox.y * scaleY;
    const sSize = cropBox.size * Math.max(scaleX, scaleY);

    const outputSize = 400;
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, outputSize, outputSize);

    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], 'profile-cropped.jpg', { type: 'image/jpeg' });
        onSave(croppedFile);
      }
    }, 'image/jpeg', 0.92);
  };

  if (!isOpen) return null;

  const displayUrl = previewUrl || resolveProfilePicUrl(currentPicUrl);

  return (
    <div className="ppm-overlay" onClick={onClose}>
      <div className="ppm-modal" onClick={e => e.stopPropagation()}>
        <button className="ppm-close" onClick={onClose}>✕</button>

        {/* VIEW MODE */}
        {mode === 'view' && (
          <div className="ppm-view">
            <div className="ppm-preview-area">
              {currentPicUrl ? (
                <img src={resolveProfilePicUrl(currentPicUrl)} alt="Profile" className="ppm-preview-img" />
              ) : (
                <div className="ppm-no-pic">
                  <div className="ppm-initials">{initials}</div>
                  <p>No profile picture set</p>
                </div>
              )}
            </div>
            <div className="ppm-icon-actions">
              <button className="ppm-icon-btn" onClick={() => fileInputRef.current?.click()} title={currentPicUrl ? 'Change Photo' : 'Upload Photo'}>
                <span className="ppm-icon-emoji">📷</span>
              </button>
              {currentPicUrl && (
                <button className="ppm-icon-btn" onClick={handleCropExisting} title="Crop & Adjust">
                  <span className="ppm-icon-emoji">✂️</span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* CROP MODE */}
        {mode === 'crop' && displayUrl && (
          <div className="ppm-crop">
            <p className="ppm-crop-hint">Drag the square to reposition. Drag the corner handle to resize.</p>
            <div
              className="ppm-crop-container"
              ref={cropAreaRef}
              onMouseMove={handleCropMouseMove}
              onTouchMove={handleCropMouseMove}
            >
              <img
                ref={imgRef}
                src={displayUrl}
                alt="Crop"
                className="ppm-crop-img"
                onLoad={handleImageLoad}
                draggable={false}
              />
              {/* Dim overlay outside crop */}
              <div className="ppm-crop-dim ppm-crop-dim-top" style={{ height: cropBox.y }} />
              <div className="ppm-crop-dim ppm-crop-dim-bottom" style={{ top: cropBox.y + cropBox.size, height: Math.max(0, imgDims.h - cropBox.y - cropBox.size) }} />
              <div className="ppm-crop-dim ppm-crop-dim-left" style={{ top: cropBox.y, height: cropBox.size, width: cropBox.x }} />
              <div className="ppm-crop-dim ppm-crop-dim-right" style={{ top: cropBox.y, height: cropBox.size, left: cropBox.x + cropBox.size, width: Math.max(0, imgDims.w - cropBox.x - cropBox.size) }} />
              {/* Crop selection box */}
              <div
                className="ppm-crop-box"
                style={{
                  left: cropBox.x,
                  top: cropBox.y,
                  width: cropBox.size,
                  height: cropBox.size
                }}
                onMouseDown={handleCropMouseDown}
                onTouchStart={handleCropMouseDown}
              >
                <div className="ppm-crop-handle" />
                {/* Grid lines */}
                <div className="ppm-crop-grid" />
              </div>
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="ppm-crop-actions">
              <button
                className="ppm-icon-btn ppm-icon-btn-accent"
                onClick={previewFile ? applyCrop : applyCropExisting}
                disabled={uploading}
                title="Apply & Save"
              >
                <span className="ppm-icon-emoji">{uploading ? '⏳' : '✅'}</span>
              </button>
              <button
                className="ppm-icon-btn"
                onClick={previewFile ? handleSaveWithoutCrop : () => setMode('view')}
                disabled={uploading}
                title={previewFile ? 'Save Without Crop' : 'Back'}
              >
                <span className="ppm-icon-emoji">{previewFile ? '⬆️' : '↩️'}</span>
              </button>
              <button className="ppm-icon-btn ppm-icon-btn-dim" onClick={() => { setMode('view'); setPreviewFile(null); if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; } setPreviewUrl(null); }} title="Cancel">
                <span className="ppm-icon-emoji">✕</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePicModal;
